export interface FacilitySchedule {
    dayOfWeek: string; // "Sunday", "Monday", etc.
    startTime: string; // Format: "HH:MM"
    endTime: string;   // Format: "HH:MM"
  }
  
  export interface Facility {
    facilityType: string;
    name: string;
    description: string;
    bookingIntervalMins: number;
    timezone: string;
    clientName: string;
    metadata: Record<string, any>;
    // FORT-401 — false means the facility is out of service and rejects new bookings.
    isActive: boolean;
  }

  export const getFacilitySchedules = (facility: Facility): FacilitySchedule[] => {
    const schedules = facility.metadata["FacilityStartEndTimes"]; // Using the numeric value for FacilityStartEndTimes (6)
    return schedules ? schedules as FacilitySchedule[] : [];
  };

  // Default booking duration (in minutes) used to pre-select the end time when
  // creating a booking. Prefers the facility's DefaultBookingLengthMins metadata
  // (FacilityMetadataType value 8); falls back to a 60-minute default for Squash
  // courts, otherwise a single booking interval, preserving the legacy behaviour
  // for facilities that haven't configured the metadata.
  export const getDefaultBookingLengthMins = (facility: Facility): number => {
    const raw = facility.metadata["DefaultBookingLengthMins"];
    // Parse strictly: accept a number, or a string that is *wholly* numeric.
    // Number("90mins") is NaN (unlike parseInt, which would yield 90), so
    // partially-numeric junk falls through to the default rather than silently
    // becoming a valid-looking duration. Require a positive whole number of minutes.
    const parsed = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
    return facility.facilityType === "Squash" ? 60 : facility.bookingIntervalMins;
  };
  
  // Get schedule for a specific date
  export function getScheduleForDay(facility: Facility, date: Date): FacilitySchedule | null {
    const schedules = getFacilitySchedules(facility);
    
    // Get day name from the date
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = dayNames[date.getDay()];
    
    // Find matching schedule
    return schedules.find(schedule => schedule.dayOfWeek === dayName) || null;
  }
  
  // Parse time string into Date object
  export function parseTimeString(timeString: string, date: Date = new Date()): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);
    return result;
  }